import { useState } from 'react';
import { useAuthStore } from '../../auth/store';
import { usePermissions, Permission } from '../../../shared/hooks/usePermissions';
import { Modal } from '../../../shared/components/Modal';
import Button from '../../../shared/components/Button';
import {
  useArchiveCharterAddon,
  useArchiveCharterPackage,
  useCharterAddons,
  useCharterPackages,
  useCharterServices,
  useCreateCharterAddon,
  useCreateCharterPackage,
  useUpdateCharterAddon,
  useUpdateCharterPackage,
} from '../hooks/useCharter';
import { PackageForm } from '../components/PackageForm';
import { AddonForm } from '../components/AddonForm';
import { AddonSourceBadge } from '../components/CharterStatusBadge';
import {
  CharterHeader,
  EmptyState,
  ErrorState,
  LoadingState,
  SelectMerchantPrompt,
  TableCard,
  TD,
  TD_MUTED,
  TH,
} from '../components/CharterPage';
import { depositRuleLabel, formatPrice } from '../components/charterFormat';
import type {
  CharterAddon,
  CharterPackage,
  CreateAddonPayload,
  CreatePackagePayload,
} from '../types';

type Tab = 'packages' | 'addons';

export function CharterCataloguePage() {
  const selectedTenantId = useAuthStore((s) => s.selectedTenantId);
  const { hasPermission } = usePermissions();
  const canManage = hasPermission(Permission.CHARTER_MANAGE);

  const [tab, setTab] = useState<Tab>('packages');

  const { data: services = [] } = useCharterServices(selectedTenantId);
  const packagesQuery = useCharterPackages(selectedTenantId);
  const addonsQuery = useCharterAddons(selectedTenantId);
  const packages = packagesQuery.data ?? [];
  const addons = addonsQuery.data ?? [];

  const createPackage = useCreateCharterPackage();
  const updatePackage = useUpdateCharterPackage();
  const archivePackage = useArchiveCharterPackage();
  const createAddon = useCreateCharterAddon();
  const updateAddon = useUpdateCharterAddon();
  const archiveAddon = useArchiveCharterAddon();

  const [pkgModal, setPkgModal] = useState(false);
  const [editingPkg, setEditingPkg] = useState<CharterPackage | null>(null);
  const [addonModal, setAddonModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState<CharterAddon | null>(null);

  const serviceName = (id: string) => services.find((s) => s.id === id)?.name ?? id;

  const savingPkg = createPackage.isPending || updatePackage.isPending;
  const savingAddon = createAddon.isPending || updateAddon.isPending;

  const submitPackage = async (values: CreatePackagePayload) => {
    if (editingPkg) await updatePackage.mutateAsync({ id: editingPkg.id, data: values });
    else await createPackage.mutateAsync(values);
    setPkgModal(false);
    setEditingPkg(null);
  };

  const submitAddon = async (values: CreateAddonPayload) => {
    if (editingAddon) await updateAddon.mutateAsync({ id: editingAddon.id, data: values });
    else await createAddon.mutateAsync(values);
    setAddonModal(false);
    setEditingAddon(null);
  };

  const newAction =
    canManage && selectedTenantId ? (
      <Button
        size="sm"
        className="h-[40px]"
        onClick={() => {
          if (tab === 'packages') {
            setEditingPkg(null);
            setPkgModal(true);
          } else {
            setEditingAddon(null);
            setAddonModal(true);
          }
        }}
      >
        {tab === 'packages' ? 'New package' : 'New add-on'}
      </Button>
    ) : undefined;

  const query = tab === 'packages' ? packagesQuery : addonsQuery;

  return (
    <div className="py-6 max-w-6xl mx-auto px-2">
      <CharterHeader
        title="Charter · Packages & Add-ons"
        subtitle="Bundle services into packages, and offer add-ons that extend price and session length."
        action={newAction}
      />

      {!selectedTenantId ? (
        <SelectMerchantPrompt what="their Charter packages and add-ons" />
      ) : (
        <>
          <div className="flex gap-6 border-b border-border mb-4">
            {([['packages', 'Packages'], ['addons', 'Add-ons']] as [Tab, string][]).map(([value, label]) => (
              <button
                key={value}
                onClick={() => setTab(value)}
                className={`pb-2 -mb-px text-sm font-medium border-b-2 transition-colors ${
                  tab === value
                    ? 'border-[#28282A] text-foreground'
                    : 'border-transparent text-muted-foreground hover:text-foreground'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {query.isLoading ? (
            <LoadingState label={`Loading ${tab}…`} />
          ) : query.error ? (
            <ErrorState label={`Failed to load ${tab}.`} />
          ) : tab === 'packages' ? (
            packages.length === 0 ? (
              <EmptyState label={`No packages yet.${canManage ? ' Click "New package" to create one.' : ''}`} />
            ) : (
              <TableCard>
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border bg-muted text-left">
                      <th className={TH}>Name</th>
                      <th className={TH}>Services</th>
                      <th className={TH}>Price</th>
                      <th className={TH}>Deposit</th>
                      <th className={TH}>Slot</th>
                      {canManage && <th className={TH}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {packages.map((p) => (
                      <tr key={p.id} className="hover:bg-muted transition-colors">
                        <td className={`${TD} font-medium`}>{p.name}</td>
                        <td className={`${TD_MUTED} max-w-[240px] truncate`} title={p.serviceIds.map(serviceName).join(', ')}>
                          {p.serviceIds.map(serviceName).join(', ')}
                        </td>
                        <td className={TD}>{formatPrice(p.price, p.currency)}</td>
                        <td className={TD_MUTED}>{depositRuleLabel(p.depositRule)}</td>
                        <td className={TD_MUTED}>{p.requiresSlot ? 'Required' : 'Async'}</td>
                        {canManage && (
                          <td className="px-6 py-4 space-x-4">
                            <button
                              onClick={() => {
                                setEditingPkg(p);
                                setPkgModal(true);
                              }}
                              className="text-xs text-blue-600 hover:underline"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => {
                                if (window.confirm(`Archive "${p.name}"?`)) archivePackage.mutate(p.id);
                              }}
                              className="text-xs text-red-600 hover:underline"
                            >
                              Archive
                            </button>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </TableCard>
            )
          ) : addons.length === 0 ? (
            <EmptyState label={`No add-ons yet.${canManage ? ' Click "New add-on" to create one.' : ''}`} />
          ) : (
            <TableCard>
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border bg-muted text-left">
                    <th className={TH}>Name</th>
                    <th className={TH}>Price</th>
                    <th className={TH}>Extra time</th>
                    <th className={TH}>Source</th>
                    {canManage && <th className={TH}>Actions</th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {addons.map((a) => (
                    <tr key={a.id} className="hover:bg-muted transition-colors">
                      <td className={`${TD} font-medium`}>{a.name}</td>
                      <td className={TD}>{formatPrice(a.price, a.currency)}</td>
                      <td className={TD_MUTED}>{a.extraDurationMinutes ? `+${a.extraDurationMinutes} min` : '—'}</td>
                      <td className="px-6 py-4">
                        <AddonSourceBadge source={a.source} />
                      </td>
                      {canManage && (
                        <td className="px-6 py-4 space-x-4">
                          {/* Merchants can only edit their own add-ons; TCS-admin add-ons are managed from the admin section. */}
                          {a.source === 'MERCHANT' ? (
                            <>
                              <button
                                onClick={() => {
                                  setEditingAddon(a);
                                  setAddonModal(true);
                                }}
                                className="text-xs text-blue-600 hover:underline"
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => {
                                  if (window.confirm(`Archive "${a.name}"?`)) archiveAddon.mutate(a.id);
                                }}
                                className="text-xs text-red-600 hover:underline"
                              >
                                Archive
                              </button>
                            </>
                          ) : (
                            <span className="text-xs text-muted-foreground">Managed by TCS</span>
                          )}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableCard>
          )}
        </>
      )}

      <Modal isOpen={pkgModal} onClose={() => setPkgModal(false)} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">{editingPkg ? 'Edit package' : 'New package'}</h2>
            <button onClick={() => setPkgModal(false)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
              &times;
            </button>
          </div>
          <PackageForm
            initial={editingPkg}
            services={services}
            onSubmit={submitPackage}
            onCancel={() => setPkgModal(false)}
            isSaving={savingPkg}
          />
        </div>
      </Modal>

      <Modal isOpen={addonModal} onClose={() => setAddonModal(false)} size="md">
        <div className="p-8">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-semibold text-foreground">{editingAddon ? 'Edit add-on' : 'New add-on'}</h2>
            <button onClick={() => setAddonModal(false)} className="text-muted-foreground hover:text-foreground text-2xl leading-none">
              &times;
            </button>
          </div>
          <AddonForm
            initial={editingAddon}
            packages={packages}
            onSubmit={submitAddon}
            onCancel={() => setAddonModal(false)}
            isSaving={savingAddon}
          />
        </div>
      </Modal>
    </div>
  );
}
